"use client";

import { useMemo, useState } from "react";
import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ModTable,
  OperationResults,
  RemainderResults,
} from "@/components/tools/arithmetic/modulo-parts";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type Convention,
  MAX_TABLE_MODULUS,
  type ModOperation,
  type TableOperation,
} from "@/lib/tool-kit/arithmetic/modulo";
import {
  computeOperations,
  computeRemainder,
} from "@/lib/tool-kit/arithmetic/modulo-view";

/** English strings. A translated content file overrides these through `ui`. */
export const MODULO_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modeRemainder: "Remainder, a mod n",
  modeOperations: "Modular arithmetic",
  modeTable: "Mod n table",
  aLabel: "Number, a",
  aHint: "Negative numbers and decimals are fine.",
  nLabel: "Modulus, n",
  nHint:
    "The number you divide by. It can be negative or a decimal, but not zero.",
  conventionLabel: "Convention for the headline answer",
  nameEuclidean: "Euclidean",
  nameFloored: "Floored",
  nameTruncated: "Truncated",
  optionEuclidean: "Euclidean — never negative",
  optionFloored: "Floored — sign of n (Python, Excel)",
  optionTruncated: "Truncated — sign of a (C, Java, JavaScript)",
  usedEuclidean: "Mathematics texts for n > 0; Rust rem_euclid",
  usedFloored: "Python and Ruby %, Excel MOD, Java Math.floorMod, Kotlin mod",
  usedTruncated: "C, C++, Java, JavaScript, Go, PHP, Swift and Rust %",
  results: "Results",
  headline: "{a} mod {n}",
  quotient: "Quotient, q",
  divisible: "Is a divisible by n?",
  yes: "Yes",
  no: "No",
  compareHeading: "The three conventions side by side",
  columnConvention: "Convention",
  columnQuotient: "Quotient q",
  columnRemainder: "Remainder r",
  columnCheck: "Check: q × n + r = a",
  columnUsed: "Used by",
  agree:
    "All three conventions give {r} here: when a and n are both positive, or when a is a multiple of n, they coincide.",
  differ:
    "The conventions differ because a or n is negative. Truncated rounds the quotient toward zero, floored rounds it down, and Euclidean keeps the remainder at 0 or above and below |n|.",
  enterRemainder: "Enter a number and a modulus to see the remainder.",
  invalid: "Enter plain numbers such as 17, −7 or 5.5.",
  zeroModulus:
    "The modulus cannot be zero: there is no remainder when dividing by 0.",
  opLabel: "Operation",
  opAdd: "Addition",
  opSubtract: "Subtraction",
  opMultiply: "Multiplication",
  opPower: "Power",
  bLabel: "Second number, b",
  bHintPower: "The exponent. Must be 0 or more.",
  bHint: "A whole number.",
  modulusLabel: "Modulus, n",
  modulusHint: "A whole number of at least 2.",
  operationHeadline: "Result mod {n}",
  beforeReducing: "Before reducing",
  rawLine: "{a} {op} {b} = {raw}, and {raw} mod {n} = {result}.",
  reduceFirst:
    "You can reduce first: {a} ≡ {aR} and {b} ≡ {bR} (mod {n}), so {aR} {op} {bR} gives the same residue, {result}.",
  powerLine:
    "{a}^{b} mod {n} = {result}. Repeated squaring keeps every number below {n} instead of building the full power.",
  squaringHeading: "Square-and-multiply",
  squaringIntro:
    "Square the base repeatedly, reducing mod n each time; multiply together the squares whose binary digit in the exponent is 1.",
  columnPower: "Power",
  columnValue: "Value mod n",
  columnUsedFlag: "Used?",
  powerCell: "{a}^{e}",
  enterOperation: "Enter two whole numbers and a modulus to see the result.",
  invalidWhole: "Use whole numbers only (no decimals) in this mode.",
  badModulus: "The modulus must be a whole number of at least 2.",
  negativeExponent:
    "A negative exponent needs a modular inverse, which exists only when a and n share no factor. Use the Modular Inverse Calculator.",
  tooBig: "Please use numbers of at most 400 digits.",
  symbolAdd: "+",
  symbolSubtract: "−",
  symbolMultiply: "×",
  symbolPower: "^",
  tableModulusLabel: "Modulus, n",
  tableOpLabel: "Table of",
  tableAdd: "Addition",
  tableMultiply: "Multiplication",
  tableAddNote:
    "Each cell is (row + column) mod {n}. Highlighted cells are 0: the pairs that add up to a multiple of {n}, so each is the other’s additive inverse.",
  tableMulNote:
    "Each cell is (row × column) mod {n}. Highlighted cells are 1: the pairs that multiply to 1, each the other’s multiplicative inverse. A row with no 1 belongs to a number that shares a factor with {n}.",
  tableCaption: "Table of {symbol} mod {n}",
} as const;

type Mode = "remainder" | "operations" | "table";

const TABLE_SIZES = Array.from(
  { length: MAX_TABLE_MODULUS - 1 },
  (_, i) => i + 2
);

export default function ModuloCalculatorWidget() {
  const strings = useToolUi(MODULO_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<Mode>("remainder");
  const [a, setA] = useState("-7");
  const [n, setN] = useState("3");
  const [convention, setConvention] = useState<Convention>("euclidean");
  const [opA, setOpA] = useState("123");
  const [opB, setOpB] = useState("45");
  const [opN, setOpN] = useState("7");
  const [operation, setOperation] = useState<ModOperation>("multiply");
  const [tableN, setTableN] = useState("7");
  const [tableOp, setTableOp] = useState<TableOperation>("multiply");

  const remainder = useMemo(() => computeRemainder(a, n), [a, n]);
  const operations = useMemo(
    () => computeOperations(operation, opA, opB, opN),
    [operation, opA, opB, opN]
  );
  const words = {
    ...strings,
    symbols: {
      add: strings.symbolAdd,
      subtract: strings.symbolSubtract,
      multiply: strings.symbolMultiply,
      power: strings.symbolPower,
    },
  };

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="mc-mode"
        onChange={setMode}
        options={[
          { value: "remainder", label: strings.modeRemainder },
          { value: "operations", label: strings.modeOperations },
          { value: "table", label: strings.modeTable },
        ]}
        value={mode}
      />
      {mode === "remainder" ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              hint={strings.aHint}
              id="mc-a"
              label={strings.aLabel}
              mono
              numeric
              onChange={setA}
              value={a}
            />
            <TextField
              hint={strings.nHint}
              id="mc-n"
              label={strings.nLabel}
              mono
              numeric
              onChange={setN}
              value={n}
            />
          </div>
          <SelectField
            id="mc-convention"
            label={strings.conventionLabel}
            onChange={setConvention}
            options={[
              { value: "euclidean", label: strings.optionEuclidean },
              { value: "floored", label: strings.optionFloored },
              { value: "truncated", label: strings.optionTruncated },
            ]}
            value={convention}
          />
        </>
      ) : null}
      {mode === "operations" ? (
        <>
          <PillGroup
            label={strings.opLabel}
            name="mc-op"
            onChange={setOperation}
            options={[
              { value: "add", label: strings.opAdd },
              { value: "subtract", label: strings.opSubtract },
              { value: "multiply", label: strings.opMultiply },
              { value: "power", label: strings.opPower },
            ]}
            value={operation}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              hint={strings.bHint}
              id="mc-op-a"
              label={strings.aLabel}
              mono
              numeric
              onChange={setOpA}
              value={opA}
            />
            <TextField
              hint={operation === "power" ? strings.bHintPower : strings.bHint}
              id="mc-op-b"
              label={strings.bLabel}
              mono
              numeric
              onChange={setOpB}
              value={opB}
            />
            <TextField
              hint={strings.modulusHint}
              id="mc-op-n"
              label={strings.modulusLabel}
              mono
              numeric
              onChange={setOpN}
              value={opN}
            />
          </div>
        </>
      ) : null}
      {mode === "table" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="mc-table-n"
            label={strings.tableModulusLabel}
            onChange={setTableN}
            options={TABLE_SIZES.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
            value={tableN}
          />
          <PillGroup
            label={strings.tableOpLabel}
            name="mc-table-op"
            onChange={setTableOp}
            options={[
              { value: "add", label: strings.tableAdd },
              { value: "multiply", label: strings.tableMultiply },
            ]}
            value={tableOp}
          />
        </div>
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "remainder" ? (
          <RemainderResults
            convention={convention}
            outcome={remainder}
            strings={strings}
          />
        ) : null}
        {mode === "operations" ? (
          <OperationResults outcome={operations} strings={words} />
        ) : null}
        {mode === "table" ? (
          <ModTable n={Number(tableN)} operation={tableOp} strings={strings} />
        ) : null}
      </section>
    </>
  );
}
